// Modal de opções de exportação em PDF. Espelha o pdf-options-modal do
// Simulador legado — escolha de modo (com/sem resolução / só resolução),
// layout (inline/separate/end) e gabarito final.
//
// Confirmação: monta URL pro endpoint /api/student/bank-export-pdf, escreve
// a resposta HTML num iframe oculto e dispara print(). O endpoint inclui
// CDN do KaTeX com auto-render — esperamos um tiquinho pra rodar antes do
// print.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { X, FileDown, Loader2 } from "lucide-react";
import { Switch } from "@app/ui/components/switch";
import { useModalGrowShrink } from "./modalOrigin";

type PdfMode = "full" | "questions-only" | "resolutions-only";
type LayoutMode = "inline" | "separate" | "end";

interface State {
  bankId: string;
  bankName: string;
  /** Versão do banco: "mc" (default, MC original) ou "essay"
   *  (dissertativa gerada por IA). Backend escolhe payload e layout. */
  version: "mc" | "essay";
}

const open = signal<State | null>(null);

export function openPdfExportModal(
  bankId: string,
  bankName: string,
  version: "mc" | "essay" = "mc"
) {
  open.value = { bankId, bankName, version };
}

export function closePdfExportModal() {
  open.value = null;
}

// Esc fecha
if (typeof window !== "undefined") {
  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Escape" && open.value) {
      open.value = null;
    }
  });
}

export function PdfExportModal() {
  const state = open.value;
  const isOpen = !!state;
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } = useModalGrowShrink(
    isOpen,
    () => closePdfExportModal()
  );

  if (!state) return null;
  return (
    <div
      class="fixed inset-0 z-[60] grid min-h-full place-items-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm"
      style={{ animation: isClosing ? "modal-backdrop-out 180ms forwards" : "modal-backdrop-in 200ms" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        ref={ref}
        class="w-full max-w-2xl rounded-md border border-border bg-card shadow-2xl"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
      >
        <PdfExportContent state={state} requestClose={requestClose} />
      </div>
    </div>
  );
}

function PdfExportContent({
  state,
  requestClose
}: {
  state: State;
  requestClose: () => void;
}) {
  // Defaults espelhando o Simulador legado: full + inline + answerKey
  const [pdfMode, setPdfMode] = useState<PdfMode>("full");
  const [layout, setLayout] = useState<LayoutMode>("inline");
  const [answerKey, setAnswerKey] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset quando reabre (signal volta com novo bankId)
  useEffect(() => {
    setPdfMode("full");
    setLayout("inline");
    setAnswerKey(true);
    setErr(null);
    setBusy(false);
  }, [state.bankId]);

  async function handleConfirm() {
    setErr(null);
    setBusy(true);
    try {
      await exportPdf({ bankId: state.bankId, bankName: state.bankName, pdfMode, layout, answerKey, version: state.version });
      requestClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const showLayout = pdfMode === "full";

  return (
    <>
      <header class="flex items-center justify-between gap-3 border-b border-border px-6 py-4">
        <div class="flex items-center gap-2.5">
          <FileDown width="1.3em" height="1.3em" className="text-primary" />
          <h2 class="text-base font-bold">Exportar para PDF</h2>
        </div>
        <button
          type="button"
          onClick={requestClose}
          aria-label="Fechar"
          class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>

      <div class="space-y-5 px-6 py-5">
        <p class="text-sm text-muted-foreground">
          Banco: <span class="font-semibold text-foreground">{state.bankName}</span>
        </p>

        <OptionGroup title="Conteúdo">
          <OptionCard
            checked={pdfMode === "full"}
            onSelect={() => setPdfMode("full")}
            title="Questões + Resoluções"
            desc="Cada questão com sua resolução logo abaixo (ou em página separada)."
          />
          <OptionCard
            checked={pdfMode === "questions-only"}
            onSelect={() => setPdfMode("questions-only")}
            title="Apenas questões"
            desc="Só o enunciado, alternativas e tabelas — sem resoluções."
          />
          <OptionCard
            checked={pdfMode === "resolutions-only"}
            onSelect={() => setPdfMode("resolutions-only")}
            title="Apenas resoluções"
            desc="Só as resoluções comentadas, agrupadas no PDF inteiro."
          />
        </OptionGroup>

        {showLayout && (
          <OptionGroup title="Layout das resoluções">
            <OptionCard
              checked={layout === "inline"}
              onSelect={() => setLayout("inline")}
              title="Logo abaixo da questão"
              desc="Resolução sai junto da questão (mesma página quando couber)."
            />
            <OptionCard
              checked={layout === "separate"}
              onSelect={() => setLayout("separate")}
              title="Em página separada"
              desc="Cada resolução começa numa página nova após a questão."
            />
            <OptionCard
              checked={layout === "end"}
              onSelect={() => setLayout("end")}
              title="Todas no final"
              desc="Questões na frente; resoluções juntas no fim do PDF."
            />
          </OptionGroup>
        )}

        <OptionGroup title="Mapa de gabarito">
          <label class="flex cursor-pointer items-center gap-3 rounded-md border border-border bg-card/40 px-4 py-3 transition-colors hover:bg-accent/30">
            <Switch
              checked={answerKey}
              onCheckedChange={(v) => setAnswerKey(v)}
            />
            <div class="flex-1">
              <div class="text-sm font-semibold">Incluir mapa de gabarito no fim</div>
              <div class="mt-0.5 text-xs text-muted-foreground">
                Lista consolidada Q1 → A, Q2 → C, etc. Útil pra revisão rápida.
              </div>
            </div>
          </label>
        </OptionGroup>

        {err && (
          <div class="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-2 text-sm text-destructive">
            {err}
          </div>
        )}
      </div>

      <footer class="flex items-center justify-end gap-2 border-t border-border bg-card/50 px-6 py-4">
        <button
          type="button"
          onClick={requestClose}
          disabled={busy}
          class="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={busy}
          class="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:brightness-110 disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 width="1.3em" height="1.3em" className="animate-spin" />
              Preparando…
            </>
          ) : (
            <>
              <FileDown width="1.3em" height="1.3em" />
              Gerar PDF
            </>
          )}
        </button>
      </footer>
    </>
  );
}

function OptionGroup({ title, children }: { title: string; children: any }) {
  return (
    <section>
      <h3 class="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <div class="space-y-2">{children}</div>
    </section>
  );
}

function OptionCard({
  checked,
  onSelect,
  title,
  desc
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      class={`flex w-full items-start gap-3 rounded-md border px-4 py-3 text-left transition-colors ${
        checked
          ? "border-primary bg-primary/5 ring-1 ring-primary/30"
          : "border-border bg-card/40 hover:bg-accent/30"
      }`}
    >
      <span
        class={`mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border-2 ${
          checked ? "border-primary" : "border-border"
        }`}
      >
        {checked && <span class="size-2 rounded-full bg-primary" />}
      </span>
      <div class="flex-1">
        <div class="text-sm font-semibold">{title}</div>
        <div class="mt-0.5 text-xs text-muted-foreground">{desc}</div>
      </div>
    </button>
  );
}

// =========================================================================
// Pipeline de geração: fetch HTML do backend → iframe oculto → print()
// =========================================================================

async function exportPdf(opts: {
  bankId: string;
  bankName: string;
  pdfMode: PdfMode;
  layout: LayoutMode;
  answerKey: boolean;
  version?: "mc" | "essay";
}) {
  const params = new URLSearchParams();
  params.set("bankId", opts.bankId);
  params.set("pdfMode", opts.pdfMode);
  if (opts.pdfMode === "full") params.set("layout", opts.layout);
  params.set("answerKey", opts.answerKey ? "1" : "0");
  if (opts.version === "essay") params.set("version", "essay");

  const res = await fetch(`/api/student/bank-export-pdf?${params.toString()}`, {
    credentials: "include"
  });
  if (!res.ok) {
    // Endpoint retorna JSON nesse caso (errorJson)
    let msg = `Erro ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.error?.message ?? msg;
    } catch { /* ignora */ }
    throw new Error(msg);
  }
  const html = await res.text();

  // Iframe oculto pra renderizar o HTML + chamar print() no contexto dele.
  // Evita "about:blank" no cabeçalho do diálogo de impressão.
  const prev = document.getElementById("_pdfPrintFrame");
  if (prev) prev.remove();
  const iframe = document.createElement("iframe");
  iframe.id = "_pdfPrintFrame";
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed; right:0; bottom:0; width:0; height:0; border:0; visibility:hidden;";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) throw new Error("Não consegui criar o iframe de impressão.");
  doc.open();
  doc.write(html);
  doc.close();

  // Espera carregar imagens + KaTeX, depois print. Mesmo padrão do legado:
  // attendamos o evento 'load' e damos margem extra pro auto-render.
  await new Promise<void>((resolve) => {
    let fired = false;
    const triggerPrint = () => {
      if (fired) return;
      fired = true;
      const imgs = doc.querySelectorAll("img");
      let pending = imgs.length;
      const fire = () => {
        // 250ms de gracia para o KaTeX auto-render terminar
        setTimeout(() => {
          try {
            const prevTitle = document.title;
            document.title = opts.bankName || prevTitle;
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            setTimeout(() => {
              document.title = prevTitle;
            }, 100);
          } catch (err) {
            console.error("[pdf] print falhou:", err);
          }
          resolve();
        }, 250);
      };
      if (pending === 0) {
        fire();
        return;
      }
      imgs.forEach((img) => {
        const next = () => {
          if (--pending === 0) fire();
        };
        if (img.complete) next();
        else {
          img.addEventListener("load", next);
          img.addEventListener("error", next);
        }
      });
    };
    iframe.addEventListener("load", triggerPrint, { once: true });
    // Fallback caso 'load' não dispare após document.write em alguns browsers
    setTimeout(triggerPrint, 1500);
  });
}
